import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import SearchFilter from "./SearchFilter";

const dummyInteractions = [
  {
    Timestamp: "Mar 16, 2025, 10:00 PM",
    Details: "Contract #12345 signed",
    Attachments: "linktoattachment.com/contract",
    Type: "Contact Signed",
  },
  {
    Timestamp: "Mar 16, 2025, 10:00 PM",
    Details: "RFQ for steel component was sent",
    Attachments: "linktoattachment.com/rfq",
    Type: "RFQ Sent",
  },
  {
    Timestamp: "Mar 16, 2025, 10:00 PM",
    Details: "Document was uploaded, waiting to be signed",
    Attachments: "linktoattachment.com/document",
    Type: "Document Uploaded",
  },
  {
    Timestamp: "Mar 16, 2025, 10:00 PM",
    Details: "Scheduled meeting with John Doe on Mar 18, 2025 at 2 PM",
    Attachments: "linktoattachment.com/meeting",
    Type: "Meeting Scheduled",
  },
  {
    Timestamp: "Mar 16, 2025, 10:00 PM",
    Details: "Invoice sent by John Doe",
    Attachments: "invoice.pdf",
    Type: "Invoice Received",
  },
];

const evaluations = [
  {
    evaluator: "Alice Johnson",
    date: new Date("2025-03-16T22:00:00"),
    type: "Performance Review",
    rating: 8.8,
    feedback: "Consistently delivers quality work and meets deadlines.",
  },
  {
    evaluator: "Bob Smith",
    date: new Date("2025-04-10T15:30:00"),
    type: "Peer Review",
    rating: 7.5,
    feedback: "Good communication but needs to focus on details.",
  },
  {
    evaluator: "Charlie Brown",
    date: new Date("2025-05-02T09:15:00"),
    type: "Project Evaluation",
    rating: 9.2,
    feedback: "Excellent leadership and problem-solving skills.",
  },
  {
    evaluator: "Diana Prince",
    date: new Date("2025-06-21T18:45:00"),
    type: "Annual Review",
    rating: 8.0,
    feedback: "Strong overall performance with room to grow in strategy.",
  },
];

const documents = [
  {
    name: "Q1 Report.pdf",
    type: "PDF",
    uploaded: new Date("2025-03-10T14:30:00"),
    uploadedBy: "Alice Johnson",
  },
  {
    name: "Project Plan.docx",
    type: "Word Document",
    uploaded: new Date("2025-04-02T09:15:00"),
    uploadedBy: "Bob Smith",
  },
  {
    name: "Team Photo.png",
    type: "Image",
    uploaded: new Date("2025-05-22T11:45:00"),
    uploadedBy: "Charlie Brown",
  },
];

const typeColors = {
  "Contact Signed": "bg-green-100 text-green-700",
  "RFQ Sent": "bg-orange-100 text-orange-700",
  "Document Uploaded": "bg-blue-100 text-blue-700",
  "Meeting Scheduled": "bg-purple-100 text-purple-700",
  "Invoice Received": "bg-red-100 text-red-700",
};

function ThreeDotMenu() {
  return (
    <button className="p-2 hover:bg-gray-100 rounded-full">
      <MoreHorizontal className="w-5 h-5 text-gray-500" />
    </button>
  );
}

export default function SupplierInteractionHistory({ onBack }) {
  const [activeTab, setActiveTab] = useState("Interactions");

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="text-black p-4 mb-4 text-left flex justify-start text-2xl"
      >
        ← Supplier Interaction History
      </button>

      {/* Header */}
      <div className="flex justify-between items-center border rounded-lg p-4 bg-gray-50 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gray-200 rounded"></div>
          <div>
            <h2 className="text-2xl text-left text-[#1D2739]">John Doe</h2>
            <p className="text-sm text-[#1D2739] text-left">
              Hardware Solutions
            </p>
            <p className="text-sm text-[#1D2739] text-left">
              San Francisco, CA, USA
            </p>
          </div>
        </div>
        <div className="flex flex-col ">
          <span className="text-base text-black">Contact information:</span>
          <p className="text-base text-[#7E7E7E] text-left">921-123-4567</p>
          <p className="text-base text-[#7E7E7E] text-left">jdoe@talkdei.io</p>
        </div>

        <div className="flex flex-row items-center gap-4 text-left">
          <p className="text-2xl font-medium text-left text-black">
            Overall Rating:
          </p>
          <p className="text-2xl font-bold text-green-600 text-left">8.8</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 mb-8">
        {["Interactions", "Evaluations", "Documents"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 ${
              activeTab === tab
                ? "border-b-2 border-[#0088FF]"
                : "text-[#DCDCDC]"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Interactions Table */}
      {activeTab === "Interactions" && (
        <>
          <SearchFilter expandData={dummyInteractions} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 w-[18%]">Timestamp</th>
                  <th className="p-3 w-[40%]">Details</th>
                  <th className="p-3 w-[23%]">Attachments</th>
                  <th className="p-3 w-[15%]">Activity Type</th>
                  <th className="p-3 w-[4%]"></th>
                </tr>
              </thead>
              <tbody>
                {dummyInteractions.map((row, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="p-3">{row.Timestamp}</td>
                    <td className="p-3 whitespace-normal break-words">
                      {row.Details}
                    </td>
                    <td className="p-3 text-blue-600 underline cursor-pointer">
                      {row.Attachments}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          typeColors[row.type]
                        }`}
                      >
                        {row.Type}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <ThreeDotMenu />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "Evaluations" && (
        <>
          <SearchFilter expandData={evaluations}  />
          <div className="p-6">
            <div className="flex flex-wrap gap-4">
              {evaluations.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F7F7F7] rounded-xl shadow-sm flex flex-col items-center gap-6"
                  style={{ width: "350px", padding: "40px 16px" }}
                >
                  <div className="flex flex-col w-full gap-2 text-[1D2739]">
                    {/* Evaluator */}
                    <div className="flex justify-start gap-4">
                      <span className="font-medium text-xl">Evaluator:</span>
                      <span className="font-medium text-xl">
                        {item.evaluator}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="flex justify-start gap-4">
                      <span>Date:</span>
                      <span>
                        {item.date.toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </div>

                    {/* Type */}
                    <div className="flex justify-start gap-4">
                      <span>Evaluation Type:</span>
                      <span>{item.type}</span>
                    </div>

                    {/* Rating */}
                    <div className="flex justify-start gap-4">
                      <span>Rating:</span>
                      <span>{item.rating.toFixed(1)}</span>
                    </div>
                    {/* Feedback */}
                    <div className="text-left">
                      <span>Feedback: </span>
                      <span>{item.feedback}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {activeTab === "Documents" && (
        <>
          <SearchFilter expandData={documents}  />
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3">Document Name</th>
                    <th className="p-3">Document Type</th>
                    <th className="p-3">Date Uploaded</th>
                    <th className="p-3">Uploaded By</th>
                    <th className="p-3 text-center">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr
                      key={index}
                      className="border-b last:border-0 hover:bg-gray-50"
                    >
                      <td className="p-3">{doc.name}</td>
                      <td className="p-3">{doc.type}</td>
                      <td className="p-3">
                        {doc.uploaded.toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </td>
                      <td className="p-3">{doc.uploadedBy}</td>
                      <td className="p-3 text-center">
                        <button className="hover:opacity-80">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 15.575C11.8667 15.575 11.7417 15.5542 11.625 15.5125C11.5083 15.4708 11.4 15.4 11.3 15.3L7.7 11.7C7.5 11.5 7.40417 11.2667 7.4125 11C7.42083 10.7333 7.51667 10.5 7.7 10.3C7.9 10.1 8.1375 9.99583 8.4125 9.9875C8.6875 9.97917 8.925 10.075 9.125 10.275L11 12.15V5C11 4.71667 11.0958 4.47917 11.2875 4.2875C11.4792 4.09583 11.7167 4 12 4C12.2833 4 12.5208 4.09583 12.7125 4.2875C12.9042 4.47917 13 4.71667 13 5V12.15L14.875 10.275C15.075 10.075 15.3125 9.97917 15.5875 9.9875C15.8625 9.99583 16.1 10.1 16.3 10.3C16.4833 10.5 16.5792 10.7333 16.5875 11C16.5958 11.2667 16.5 11.5 16.3 11.7L12.7 15.3C12.6 15.4 12.4917 15.4708 12.375 15.5125C12.2583 15.5542 12.1333 15.575 12 15.575ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V16C4 15.7167 4.09583 15.4792 4.2875 15.2875C4.47917 15.0958 4.71667 15 5 15C5.28333 15 5.52083 15.0958 5.7125 15.2875C5.90417 15.4792 6 15.7167 6 16V18H18V16C18 15.7167 18.0958 15.4792 18.2875 15.2875C18.4792 15.0958 18.7167 15 19 15C19.2833 15 19.5208 15.0958 19.7125 15.2875C19.9042 15.4792 20 15.7167 20 16V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z"
                              fill="#0088FF"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
